import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import {
  doDelete,
  doStart,
  doStop,
  doUpdate,
  luHandleApiError,
  u3147IsSessionExported,
  u3147UnwrapSessionResponse,
  type U3147StartRequest,
  type U3147TimeSession,
  type U3147UpdateRequest,
} from './U3147Service';
import {
  u3147ComputeRoundedTimerEndIso,
  u3147ListOverlappingOlderSessions,
} from './tools/u3147FormatDuration';
import { NS } from './U3147';

export interface U3147StopTimerOptions {
  roundingMinutes: number;
  startedAtIso: string;
  organizationTimezone?: string;
  /** Instant du clic « Arrêter » (avant l'appel API). */
  stoppedAtMs?: number;
  /** Plages déjà enregistrées du jour (pour supprimer les chevauchements antérieurs). */
  existingSessions?: U3147TimeSession[];
}

function u3147TimerStopNeedsRoundingCorrection(
  session: U3147TimeSession,
  stoppedAtMs: number,
  roundingMinutes: number,
  organizationTimezone?: string,
): boolean {
  if (!session.startedAt?.trim()) return false;
  const expected = u3147ComputeRoundedTimerEndIso(
    session.startedAt,
    stoppedAtMs,
    roundingMinutes,
    organizationTimezone,
  );
  if (session.durationMinutes !== expected.durationMinutes) return true;
  const apiEndMs = session.endedAt ? new Date(session.endedAt).getTime() : Number.NaN;
  const expectedEndMs = new Date(expected.endedAt).getTime();
  if (Number.isNaN(apiEndMs) || Number.isNaN(expectedEndMs)) return true;
  return Math.abs(apiEndMs - expectedEndMs) > 1000;
}

function u3147BuildTimerStopUpdateRequest(
  session: U3147TimeSession,
  stoppedAtMs: number,
  roundingMinutes: number,
  organizationTimezone?: string,
): U3147UpdateRequest {
  const expected = u3147ComputeRoundedTimerEndIso(
    session.startedAt!,
    stoppedAtMs,
    roundingMinutes,
    organizationTimezone,
  );
  return {
    id: session.id,
    projectId: session.projectId,
    subprojectId: session.subprojectId ?? null,
    taskId: session.taskId ?? null,
    workDate: session.workDate?.slice(0, 10) ?? '',
    startedAt: session.startedAt!,
    endedAt: expected.endedAt,
    note: session.note ?? null,
    version: session.version,
  };
}

export function useU3147TimerHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const startTimer = useCallback(
    async (body: U3147StartRequest, options?: { hadOpenSession?: boolean }) => {
      setBusy(true);
      try {
        const response = await doStart(axios, body);
        const session = u3147UnwrapSessionResponse(response);
        if (!session) {
          toast.error(
            t('msgTimerStartFailed', {
              ns: NS,
              defaultValue: 'Impossible de démarrer le chronomètre (réponse serveur inattendue).',
            }),
          );
          return null;
        }
        if (options?.hadOpenSession) {
          toast.info(
            t('msgPreviousSessionStopped', {
              ns: NS,
              defaultValue: 'Session précédente arrêtée.',
            }),
          );
        }
        return session;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [axios, t, toast],
  );

  const stopTimer = useCallback(
    async (sessionId: string, options: U3147StopTimerOptions) => {
      const stoppedAtMs = options.stoppedAtMs ?? Date.now();
      setBusy(true);
      try {
        const expected = u3147ComputeRoundedTimerEndIso(
          options.startedAtIso,
          stoppedAtMs,
          options.roundingMinutes,
          options.organizationTimezone,
        );
        const overlappingOlder = u3147ListOverlappingOlderSessions(
          options.startedAtIso,
          expected.endedAt,
          options.existingSessions ?? [],
          sessionId,
        );
        for (const other of overlappingOlder) {
          if (!other.id || u3147IsSessionExported(other as U3147TimeSession)) continue;
          await doDelete(axios, other.id, {
            id: other.id,
            version: (other as U3147TimeSession).version,
          });
        }

        const response = await doStop(axios, sessionId);
        let session = u3147UnwrapSessionResponse(response);
        if (!session?.startedAt?.trim()) {
          return session;
        }
        if (u3147TimerStopNeedsRoundingCorrection(session, stoppedAtMs, options.roundingMinutes, options.organizationTimezone)) {
          const updateBody = u3147BuildTimerStopUpdateRequest(
            session,
            stoppedAtMs,
            options.roundingMinutes,
            options.organizationTimezone,
          );
          const updateResponse = await doUpdate(axios, sessionId, updateBody);
          session = u3147UnwrapSessionResponse(updateResponse) ?? session;
        }
        return session;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [axios, t, toast],
  );

  return { busy, startTimer, stopTimer };
}
